import React, { useEffect, useState, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Pagination from "@material-ui/lab/Pagination";
import Grid from "@material-ui/core/Grid";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { HourglassEmpty, Autorenew, CheckCircle } from "@material-ui/icons";
import theme from "../../theme";
import CommonTable from "../table";
import CommonButton from "../button";
import CommonInput from "../input";
import {
  getVendorPaymentsAction,
  getVendorSettlementsAction,
  addVendorBankDetailsErrorAction,
  updateVendorBankDetailsErrorAction,
  vendorSettlementAggregateAction
} from "../../containers/dashboard/action";
import CommonTab from "../tabs";
import EditMerchantDetail from "./editMerchantDetail";
import DragDatePreviewer from "../datepicker/dragDatePickerPreviewer";
import { useDispatch, useSelector } from "react-redux";
import { getCountriesAction } from "../../containers/login/action";
import { getVendorOverviewAction } from "../../containers/vendorDashboard/action";
import { moneyValue, getStartEndDate } from "../../utils";
import moment from "moment";
import Loading from "../loading";
import VendorFeesTab from "./vendorFeesTab";
import { formatCurrency } from "../adminReports/TableComponent";

const useStyles = makeStyles(() => ({
  detailContainer: {
    marginTop: "30px",
    "& .block": {
      background: theme.colors.White,
      boxShadow: "0px 2px 15px #D0CFDB99",
      borderRadius: "15px",
      padding: "30px",
      display: "flex",
      marginBottom: "15px",
      "& .left": {
        flex: "0.6",
        display: "flex",
        flexDirection: "column",
        "& .topSection": {
          display: "flex",
        },
        "& .logoContainer": {
          height: "85px",
          width: "85px",
          borderRadius: "50%",
          border: `1px solid ${theme.colors.Zumthor}`,
          backgroundColor: theme.colors.White,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& img": {
            height: "100%",
            width: "100%",
            objectFit: "contain",
            borderRadius: "50%",
          },
        },
        "& .merchantInfoContainer": {
          marginLeft: "25px",
          color: theme.colors.Violet,
          width: "60%",
          "& .merchantName": {
            fontSize: "22px",
            fontFamily: theme.fontFamily.MARKPROBOLD,
            marginBottom: "10px",
          },
          "& .flexContainer": {
            display: "flex",
            alignItems: "center",
            fontSize: "12px",
            fontFamily: theme.fontFamily.MARKPRO,
            "& .label": { fontFamily: theme.fontFamily.MARKPRO, flex: 0.5 },
            "& .val": {
              fontFamily: theme.fontFamily.MARKPROBOLD,
              flex: 0.5,
            },
          },
        },
      },
      "& .right": {
        flex: "0.4",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        "& .infoContainer": {
          color: theme.colors.Violet,
          marginBottom: "15px",
          "& .label": {
            fontSize: "12px",
            fontFamily: theme.fontFamily.MARKPRO,
          },
          "& .val": {
            fontSize: "16px",
            fontFamily: theme.fontFamily.MARKPROHEAVY,
          },
        },
        "& .borderContainer": {
          border: `1px solid ${theme.colors.Zumthor}`,
          width: "100%",
          margin: "10px 0px",
        },
      },
      "& .additionalInfo": {
        marginTop: "30px",
        paddingTop: "30px",
        borderTop: `2px solid ${theme.colors.Zumthor}`,
        width: "100%",
        fontSize: "12px",
        fontFamily: theme.fontFamily.MARKPRO,
        fontWeight: "bold",
        "& .sectionTitle": {
          fontSize: "18px",
          fontFamily: theme.fontFamily.MARKPROHEAVY,
          color: theme.colors.DodgerBlue,
          marginBottom: "30px",
          paddingBottom: "15px",
          borderBottom: `3px solid ${theme.colors.DodgerBlue}`,
          fontWeight: "bold",
          letterSpacing: "0.5px",
        },
        "& .sectionSubtitle": {
          fontSize: "14px",
          fontFamily: theme.fontFamily.MARKPROBOLD,
          color: theme.colors.Violet,
          marginTop: "25px",
          marginBottom: "18px",
          paddingBottom: "8px",
          borderBottom: `1px solid ${theme.colors.Zumthor}`,
          "&:first-of-type": {
            marginTop: "0",
          },
        },
        "& .infoGrid": {
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px",
          marginBottom: "25px",
          "@media (max-width: 768px)": {
            gridTemplateColumns: "1fr",
            gap: "15px",
          },
          "& .infoItem": {
            padding: "18px",
            background: `linear-gradient(135deg, ${theme.colors.Zumthor}15 0%, ${theme.colors.White} 100%)`,
            borderRadius: "12px",
            border: `1.5px solid ${theme.colors.Zumthor}`,
            transition: "all 0.3s ease",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
            "&:hover": {
              background: `linear-gradient(135deg, ${theme.colors.Zumthor}25 0%, ${theme.colors.White} 100%)`,
              transform: "translateY(-3px)",
              boxShadow: "0px 6px 12px rgba(0,0,0,0.1)",
              borderColor: theme.colors.DodgerBlue + "40",
            },
            "& .label": {
              fontSize: "10px",
              fontFamily: theme.fontFamily.MARKPROMEDIUM,
              color: theme.colors.Violet + "AA",
              marginBottom: "10px",
              textTransform: "uppercase",
              letterSpacing: "1px",
            },
            "& .val": {
              fontSize: "16px",
              fontFamily: theme.fontFamily.MARKPROBOLD,
              color: theme.colors.Violet,
              wordBreak: "break-word",
              lineHeight: "1.4",
            },
          },
        },
        "& .qrCodeSection": {
          marginTop: "30px",
          paddingTop: "25px",
          borderTop: `2px solid ${theme.colors.Zumthor}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          "& .qrCodeTitle": {
            fontSize: "14px",
            fontFamily: theme.fontFamily.MARKPROBOLD,
            color: theme.colors.Violet,
            marginBottom: "15px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          },
          "& .qrCodeImage": {
            width: "200px",
            height: "200px",
            objectFit: "contain",
            border: `2px solid ${theme.colors.Zumthor}`,
            borderRadius: "12px",
            padding: "15px",
            backgroundColor: theme.colors.White,
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0px 6px 16px rgba(0,0,0,0.15)",
              transform: "scale(1.02)",
            },
          },
          "& .content": {
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: "15px",
          },
        },
      },
    },
  },
  tableContainer: {
    marginTop: "30px",
    background: theme.colors.White,
    boxShadow: "0px 2px 15px #D0CFDB99",
    borderRadius: "15px",
    padding: "30px",
  },
  headerName: {
    color: theme.colors.Violet,
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
  },
  statusBadge: {
    display: "inline-block",
    padding: "8px 20px",
    borderRadius: "20px",
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    textTransform: "uppercase",
  },
  statusPending: {
    backgroundColor: theme.colors.Zumthor,
    color: theme.colors.DodgerBlue,
  },
  statusProcessing: {
    backgroundColor: theme.colors.DodgerBlue,
    color: theme.colors.White,
  },
  statusCompleted: {
    backgroundColor: theme.colors.MintGreen,
    color: theme.colors.Violet,
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    marginTop: "30px",
    marginBottom: "20px",
  },
  employeeName: {
    color: theme.colors.Violet,
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPRO,
  },
  amount: {
    color: theme.colors.Violet,
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
  },
  reference: {
    color: theme.colors.Violet,
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPRO,
  },
  date: {
    color: theme.colors.Violet,
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPRO,
  },
  intent: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: "10px",
    fontSize: "10px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    backgroundColor: theme.colors.Zumthor,
    color: theme.colors.DodgerBlue,
  },
  editButton: {
    marginTop: "15px",
    marginBottom: "15px",
  },
  tabContainer: {
    marginTop: "30px",
    marginBottom: "20px",
  },
  reportsContainer: {
    marginTop: "20px",
  },
  periodSelector: {
    display: "flex",
    gap: "15px",
    marginBottom: "30px",
    "& .periodButton": {
      padding: "10px 20px",
      borderRadius: "21px",
      border: `1px solid ${theme.colors.DodgerBlue}`,
      backgroundColor: theme.colors.White,
      color: theme.colors.DodgerBlue,
      fontFamily: theme.fontFamily.MARKPROMEDIUM,
      fontSize: "14px",
      cursor: "pointer",
      "&.active": {
        backgroundColor: theme.colors.DodgerBlue,
        color: theme.colors.White,
      },
    },
  },
  summaryCard: {
    backgroundColor: theme.colors.White,
    boxShadow: "0px 2px 15px #D0CFDB99",
    borderRadius: "10px",
    padding: "30px 40px",
    marginBottom: "20px",
    "& .cardTitle": {
      color: theme.colors.Violet,
      fontSize: "18px",
      fontFamily: theme.fontFamily.MARKPROBOLD,
      marginBottom: "20px",
    },
    "& .summaryRow": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "15px 0",
      borderBottom: `1px solid ${theme.colors.Zumthor}`,
      "&:last-child": {
        borderBottom: "none",
      },
      "& .label": {
        color: theme.colors.Violet,
        fontSize: "14px",
        fontFamily: theme.fontFamily.MARKPRO,
      },
      "& .value": {
        color: theme.colors.Violet,
        fontSize: "18px",
        fontFamily: theme.fontFamily.MARKPROHEAVY,
        "& .currency": {
          fontSize: "12px",
          position: "relative",
          bottom: "3px",
        },
      },
    },
  },
  bankSection: {
    marginTop: "30px",
    marginBottom: "10px",
    padding: "30px",
    background: theme.colors.White,
    boxShadow: "0px 2px 15px #D0CFDB99",
    borderRadius: "15px",
    "& .bankHeader": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
      "& .title": {
        color: theme.colors.DodgerBlue,
        fontFamily: theme.fontFamily.MARKPROBOLD,
        fontSize: "18px",
      },
    },
    "& .bankList": {
      display: "grid",
      gap: "15px",
    },
    "& .bankCard": {
      border: `1px solid ${theme.colors.Zumthor}`,
      borderRadius: "12px",
      padding: "20px",
      background: `linear-gradient(135deg, ${theme.colors.Zumthor}10 0%, ${theme.colors.White} 100%)`,
      "& .bankRow": {
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "15px",
        marginBottom: "10px",
        "& .label": {
          color: theme.colors.Violet + "AA",
          fontSize: "12px",
          fontFamily: theme.fontFamily.MARKPROMEDIUM,
          width: "150px",
        },
        "& .val": {
          color: theme.colors.Violet,
          fontSize: "14px",
          fontFamily: theme.fontFamily.MARKPROBOLD,
          width: "calc(100% - 150px)",
          wordBreak: "break-word",
        },
      },
      "& .cardActions": {
        display: "flex",
        gap: "10px",
        marginTop: "10px",
      },
    },
    "& .noBankMessage": {
      color: theme.colors.Violet,
      fontFamily: theme.fontFamily.MARKPRO,
      fontSize: "14px",
    },
  },
  bankDialogForm: {
    display: "grid",
    gap: "15px",
    marginTop: "10px",
  },
  aggregateSettlementSection: {
    marginTop: "30px",
    marginBottom: "10px",
    padding: "30px",
    background: theme.colors.White,
    boxShadow: "0px 2px 15px #D0CFDB99",
    borderRadius: "15px",
    "& .aggregateSettlementHeader": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
      "& .title": {
        color: theme.colors.DodgerBlue,
        fontFamily: theme.fontFamily.MARKPROBOLD,
        fontSize: "18px",
      },
    },
    "& .aggregateSettlementList": {
      display: "grid",
      gap: "15px",
    },
    "& .aggregateSettlementCard": {
      border: `1px solid ${theme.colors.Zumthor}`,
      borderRadius: "12px",
      padding: "20px",
      background: `linear-gradient(135deg, ${theme.colors.Zumthor}10 0%, ${theme.colors.White} 100%)`,
      "& .aggregateSettlementRow": {
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "15px",
        marginBottom: "10px",
        "& .label": {
          color: theme.colors.Violet + "AA",
          fontSize: "12px",
          fontFamily: theme.fontFamily.MARKPROMEDIUM,
          width: "150px",
        },
        "& .val": {
          color: theme.colors.Violet,
          fontSize: "14px",
          fontFamily: theme.fontFamily.MARKPROBOLD,
          width: "calc(100% - 150px)",
          wordBreak: "break-word",
        },
      },
      "& .cardActions": {
        display: "flex",
        gap: "10px",
        marginTop: "10px",
      },
    },
    "& .noAggregateSettlementMessage": {
      color: theme.colors.Violet,
      fontFamily: theme.fontFamily.MARKPRO,
      fontSize: "14px",
    },
  },
}));

export function getIntentDisplay(intent) {
  switch (intent) {
    case "TRANSFER":
      return "Salary Transfer"
    case "WITHDRAW":
      return "Withdrawal"
    case "PAYMENT":
      return "Payment"
    case "REFUND":
      return "Refund"
    case "CHARGE":
      return "Charge"
    case "DISCOUNT":
      return "Discount"
    case "OTHER":
      return "Other"
    case "EMPLOYEE_CREDIT":
      return "Employee Credit"
    case "VENDOR_PAYMENT":
      return "Vendor Payment"
    default:
      return intent
  }
}

function MerchantDetail({
  merchantInfo,
  getVendorPaymentsMethod,
  updateMerchantMethod,
  getMerchantInfoMethod,
  listVendorBankDetailsMethod,
  addVendorBankDetailsMethod,
  updateVendorBankDetailsMethod,
  deleteVendorBankDetailsMethod,
  getVendorBankDetailsMethod,
  vendorSettlementTriggerMethod,
  // vendors.edit gates edit + settlement actions; bank accounts & fee ranges
  // are super-only.
  canEditVendors = true,
  superUser = false,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [bankAction, setBankAction] = useState("create");
  const [editingBankId, setEditingBankId] = useState(null);
  const [bankForm, setBankForm] = useState({
    bank_name: "",
    account_number: "",
    account_name: "",
    code: "",
  });
  const prevUpdateMerchantSuccessRef = useRef(null);
  const prevAddBankSuccessRef = useRef(null);
  const prevUpdateBankSuccessRef = useRef(null);
  const prevDeleteBankSuccessRef = useRef(null);
  const prevGetBankDetailsSuccessRef = useRef(null);
  const [totalPage, setTotalPage] = useState(1);
  const [settlementsPage, setSettlementsPage] = useState(1);
  const [settlementsTotalPage, setSettlementsTotalPage] = useState(1);
  const [editMerchantDetail, setEditMerchantDetail] = useState(null);
  const page_size = 10;
  const settlements_page_size = 20;
  const [activeTab, setActiveTab] = useState("Sales");
  const [selectedPeriod, setSelectedPeriod] = useState("today");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [start_date, end_date] = getStartEndDate();
  const [defaultStartDate, setDefaultStartDate] = useState(
    start_date.toISOString().split("T")[0]
  );
  const [defaultEndDate, setDefaultEndDate] = useState(
    end_date.toISOString().split("T")[0]
  );
  const [settlementAggregate, setSettlementAggregate] = useState({
    dialogPreviewOpen: false,

  })

  const dashboardReducer = useSelector((state) => {
    return state.dashboardReducer;
  });

  const loginReducer = useSelector((state) => {
    return state.loginReducer;
  });

  const vendorDashboardReducer = useSelector((state) => {
    return state.vendorDashboardReducer;
  });

  const vendorSettlementAggregateActionMethod = useCallback(
    (data) => dispatch(vendorSettlementAggregateAction(data)),
    [dispatch]
  );

  const getCountriesMethod = useCallback(
    () => dispatch(getCountriesAction()),
    [dispatch]
  );

  const getVendorOverviewMethod = useCallback(
    (data) => dispatch(getVendorOverviewAction(data)),
    [dispatch]
  );

  const getVendorSettlementsMethod = useCallback(
    (data) => dispatch(getVendorSettlementsAction(data)),
    [dispatch]
  );

  // useEffect(() => {
  //   if (settlementAggregate.dialogPreviewOpen) {
  //     vendorSettlementAggregateActionMethod({ vendor_id: merchantInfo.id });
  //   }

  // }, [settlementAggregate, vendorSettlementAggregateActionMethod])

  useEffect(() => {
    if (!loginReducer?.countryOptions?.length) {
      getCountriesMethod();
    }
  }, [loginReducer?.countryOptions?.length, getCountriesMethod]);

  const getCountryName = (countryCode) => {
    if (!countryCode || !loginReducer?.countryOptions?.length) return countryCode || "N/A";
    const country = loginReducer.countryOptions.find((c) => c.id === countryCode);
    return country ? country.name : countryCode;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    try {
      const time = timeString.split("T")[1]?.split(".")[0] || timeString;
      return time.substring(0, 8); // HH:MM:SS
    } catch {
      return timeString;
    }
  };

  const addVendorBankDetailsErrorActionMethod = useCallback(
    (error) => dispatch(addVendorBankDetailsErrorAction(error)),
    [dispatch]
  );
  const updateVendorBankDetailsErrorActionMethod = useCallback(
    (error) => dispatch(updateVendorBankDetailsErrorAction(error)),
    [dispatch]);

  useEffect(() => {
    if (merchantInfo?.id) {
      getVendorPaymentsMethod(merchantInfo.id, { page, page_size });
    }
  }, [merchantInfo, getVendorPaymentsMethod, page, page_size]);

  useEffect(() => {
    if (activeTab === "Reports" && merchantInfo?.id) {
      let filters = {};

      if (selectedPeriod === "today") {
        const today = new Date();
        const start = new Date(today);
        start.setUTCHours(0, 0, 0, 0);
        filters.start_date = start.toISOString().split("T")[0];
        const end = new Date(today);
        end.setUTCHours(23, 59, 59, 999);
        filters.end_date = end.toISOString().split("T")[0];
      } else if (selectedPeriod === "week") {
        const today = new Date();
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        weekStart.setUTCHours(0, 0, 0, 0);
        filters.start_date = weekStart.toISOString().split("T")[0];
        const end = new Date(today);
        end.setUTCHours(23, 59, 59, 999);
        filters.end_date = end.toISOString().split("T")[0];
      } else if (selectedPeriod === "month") {
        const today = new Date();
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        monthStart.setUTCHours(0, 0, 0, 0);
        filters.start_date = monthStart.toISOString().split("T")[0];
        const end = new Date(today);
        end.setUTCHours(23, 59, 59, 999);
        filters.end_date = end.toISOString().split("T")[0];
      } else if (selectedPeriod === "custom" && startDate && endDate) {
        const start = new Date(startDate);
        start.setUTCHours(0, 0, 0, 0);
        filters.start_date = start.toISOString().split("T")[0];
        const end = new Date(endDate);
        end.setUTCHours(23, 59, 59, 999);
        filters.end_date = end.toISOString().split("T")[0];
      }
      getVendorOverviewMethod({ vendor_id: merchantInfo.id, ...filters });
    }
  }, [activeTab, selectedPeriod, startDate, endDate, getVendorOverviewMethod, merchantInfo?.id]);

  useEffect(() => {
    if (dashboardReducer?.vendorPayments?.count) {
      setTotalPage(Math.ceil(dashboardReducer.vendorPayments.count / page_size));
    }
  }, [dashboardReducer]);

  useEffect(() => {
    if (activeTab === "Settlements" && merchantInfo?.id) {
      getVendorSettlementsMethod({
        vendor_id: merchantInfo.id,
        page: settlementsPage,
        page_size: settlements_page_size,
      });
    }
  }, [activeTab, settlementsPage, getVendorSettlementsMethod, merchantInfo?.id]);

  useEffect(() => {
    if (dashboardReducer?.vendorSettlements?.count) {
      setSettlementsTotalPage(Math.ceil(dashboardReducer.vendorSettlements.count / settlements_page_size));
    }
  }, [dashboardReducer?.vendorSettlements]);

  useEffect(() => {
    if (merchantInfo?.id) {
      listVendorBankDetailsMethod({ vendor_id: merchantInfo.id });
    }
  }, [merchantInfo, listVendorBankDetailsMethod]);

  useEffect(() => {
    if (
      dashboardReducer?.addVendorBankDetailsSuccess &&
      dashboardReducer.addVendorBankDetailsSuccess !== prevAddBankSuccessRef.current
    ) {
      prevAddBankSuccessRef.current = dashboardReducer.addVendorBankDetailsSuccess;
      setBankDialogOpen(false);
      setEditingBankId(null);
      setBankForm({ bank_name: "", account_number: "", account_name: "", code: "" });
      if (merchantInfo?.id) {
        listVendorBankDetailsMethod({ vendor_id: merchantInfo.id });
      }
    }
  }, [dashboardReducer?.addVendorBankDetailsSuccess, merchantInfo, listVendorBankDetailsMethod]);

  useEffect(() => {
    if (
      dashboardReducer?.updateVendorBankDetailsSuccess &&
      dashboardReducer.updateVendorBankDetailsSuccess !== prevUpdateBankSuccessRef.current
    ) {
      prevUpdateBankSuccessRef.current = dashboardReducer.updateVendorBankDetailsSuccess;
      setBankDialogOpen(false);
      setEditingBankId(null);
      setBankForm({ bank_name: "", account_number: "", account_name: "", code: "" });
      if (merchantInfo?.id) {
        listVendorBankDetailsMethod({ vendor_id: merchantInfo.id });
      }
    }
  }, [dashboardReducer?.updateVendorBankDetailsSuccess, merchantInfo, listVendorBankDetailsMethod]);

  useEffect(() => {
    if (merchantInfo?.id) {
      listVendorBankDetailsMethod({ vendor_id: merchantInfo.id });
    }

  }, [dashboardReducer?.deleteVendorBankDetailsSuccess, merchantInfo, listVendorBankDetailsMethod]);

  useEffect(() => {
    if (
      dashboardReducer?.getVendorBankDetailsSuccess &&
      dashboardReducer.getVendorBankDetailsSuccess !== prevGetBankDetailsSuccessRef.current
    ) {
      prevGetBankDetailsSuccessRef.current = dashboardReducer.getVendorBankDetailsSuccess;
      const bankDetail = dashboardReducer.getVendorBankDetailsSuccess?.data;
      setBankForm({
        bank_name: bankDetail?.bank_name || "",
        account_number: bankDetail?.account_number || "",
        account_name: bankDetail?.account_name || "",
        code: bankDetail?.code || "",
      });
      setEditingBankId(bankDetail?.id || bankDetail?.bank_id || null);
    }
  }, [dashboardReducer?.getVendorBankDetailsSuccess, dashboardReducer?.getVendorBankDetails]);

  useEffect(() => {
    const currentUpdateMerchantSuccess = dashboardReducer?.updateMerchantSuccess;
    if (
      currentUpdateMerchantSuccess &&
      currentUpdateMerchantSuccess !== prevUpdateMerchantSuccessRef.current &&
      merchantInfo?.id
    ) {
      prevUpdateMerchantSuccessRef.current = currentUpdateMerchantSuccess;
      setEditMerchantDetail(null);
      getMerchantInfoMethod(merchantInfo.id);
    }
  }, [dashboardReducer?.updateMerchantSuccess, merchantInfo, getMerchantInfoMethod]);

  useEffect(() => {
    if (dashboardReducer?.vendorSettlementTriggerSuccess && merchantInfo?.id) {
      setSettlementAggregate({ open: false });
      setSettlementsPage(1);
      getVendorSettlementsMethod({ vendor_id: merchantInfo?.id, page: 1 });
    }
  }, [dashboardReducer?.vendorSettlementTriggerSuccess, merchantInfo, getVendorSettlementsMethod]);


  const bankList = Array.isArray(dashboardReducer?.listVendorBankDetailsSuccess)
    ? dashboardReducer.listVendorBankDetailsSuccess
    : Array.isArray(dashboardReducer?.listVendorBankDetailsSuccess?.data)
      ? dashboardReducer.listVendorBankDetailsSuccess.data
      : Array.isArray(dashboardReducer?.listVendorBankDetailsSuccess?.results)
        ? dashboardReducer.listVendorBankDetailsSuccess.results
        : [];

  const getBankId = (bank) => bank?.id || bank?.bank_id;

  const resetBankForm = () => {
    setBankForm({ bank_name: "", account_number: "", account_name: "", code: "" });
    setEditingBankId(null);
  };

  const openAddBankDialog = () => {
    setBankAction("create");
    resetBankForm();
    setBankDialogOpen(true);
  };

  const openEditBankDialog = (bank) => {
    const bankId = getBankId(bank);
    setBankAction("edit");
    setBankDialogOpen(true);
    setEditingBankId(bankId);
    getVendorBankDetailsMethod({ vendor_id: merchantInfo.id, bank_id: bankId });
  };

  const closeBankDialog = () => {
    setBankDialogOpen(false);
    resetBankForm();
    addVendorBankDetailsErrorActionMethod(null);
    updateVendorBankDetailsErrorActionMethod(null);
  };

  const handleOpenSettlementDialogPreview = () => {
    setSettlementAggregate({
      open: true
    });
    vendorSettlementAggregateActionMethod({ vendor_id: merchantInfo?.id });
  }

  const handleCloseSettlementDialogPreview = () => {
    setSettlementAggregate({
      open: false
    });

  }

  const handleBankFieldChange = (field, value) => {
    setBankForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveBank = () => {
    if (bankAction === "edit" && editingBankId) {
      updateVendorBankDetailsMethod({
        vendor_id: merchantInfo.id,
        bank_id: editingBankId,
        data: bankForm,
      });
    } else {
      addVendorBankDetailsMethod({
        vendor_id: merchantInfo.id,
        data: bankForm,
      });
    }
  };

  const handleDeleteBank = (bank) => {
    const bankId = getBankId(bank);
    if (!bankId) return;
    deleteVendorBankDetailsMethod({ vendor_id: merchantInfo.id, bank_id: bankId });
  };

  if (!merchantInfo) {
    return null;
  }

  if (editMerchantDetail) {
    return (
      <EditMerchantDetail
        editMerchantDetail={editMerchantDetail}
        setEditMerchantDetail={setEditMerchantDetail}
        updateMerchantMethod={updateMerchantMethod}
      />
    );
  }


  return (
    <div className={classes.detailContainer}>
      {canEditVendors ? (
        <div className={classes.editButton}>
          <CommonButton
            type="button"
            text="Edit Merchant Details"
            stylevariant="primary"
            onClick={() => {
              setEditMerchantDetail(merchantInfo);
            }}
            style={{ padding: "5px 30px" }}
            fontSize="14px"
          />
        </div>
      ) : null}
      <div className="block">
        <div className="left">
          <div className="logoContainer">
            {merchantInfo?.business_logo ? (
              <img src={merchantInfo.business_logo} alt={merchantInfo.business_name} />
            ) : (
              <div
                style={{
                  fontSize: "30px",
                  color: theme.colors.DodgerBlue,
                  fontFamily: theme.fontFamily.MARKPROBOLD,
                }}
              >
                {merchantInfo?.business_name?.charAt(0)?.toUpperCase() || "M"}
              </div>
            )}
          </div>
          <div className="merchantInfoContainer">
            <div className="merchantName">{merchantInfo?.business_name}</div>
            {merchantInfo?.business_description ? (
              <div className="flexContainer" style={{ marginBottom: "10px" }}>
                <div className="label">Description:</div>
                <div className="val" style={{ fontSize: "11px", fontFamily: theme.fontFamily.MARKPRO }}>
                  {merchantInfo.business_description}
                </div>
              </div>
            ) : null}
            <div className="flexContainer">
              <div className="label">Email:</div>
              <div className="val">{merchantInfo?.owner_email || merchantInfo?.email || "N/A"}</div>
            </div>
            {merchantInfo?.phone_number || merchantInfo?.phone ? (
              <div className="flexContainer">
                <div className="label">Phone:</div>
                <div className="val">{merchantInfo?.phone_number || merchantInfo?.phone}</div>
              </div>
            ) : null}
            {merchantInfo?.address ? (
              <div className="flexContainer">
                <div className="label">Address:</div>
                <div className="val">{merchantInfo.address}</div>
              </div>
            ) : null}
            {merchantInfo?.country ? (
              <div className="flexContainer">
                <div className="label">Country:</div>
                <div className="val">{getCountryName(merchantInfo.country)}</div>
              </div>
            ) : null}
          </div>

          <div className={"additionalInfo"}>
            <div className="sectionTitle">Additional Information</div>
            <div className="content">

              {(merchantInfo?.start_time || merchantInfo?.end_time) && (
                <>
                  <div className="sectionSubtitle">Operating Hours</div>
                  <div className="infoGrid">
                    {merchantInfo?.start_time ? (
                      <div className="infoItem">
                        <div className="label">Open Time</div>
                        <div className="val">{formatTime(merchantInfo.start_time)}</div>
                      </div>
                    ) : null}
                    {merchantInfo?.end_time ? (
                      <div className="infoItem">
                        <div className="label">Close Time</div>
                        <div className="val">{formatTime(merchantInfo.end_time)}</div>
                      </div>
                    ) : null}
                  </div>
                </>
              )}

              {(merchantInfo?.settlement_period_type ||
                merchantInfo?.settlement_schedules?.frequency ||
                merchantInfo?.settlement_schedules?.next_date_settlement) && (
                  <>
                    <div className="sectionSubtitle">Settlement Information</div>
                    <div className="infoGrid">
                      {merchantInfo?.settlement_period_type ? (
                        <div className="infoItem">
                          <div className="label">Settlement Period</div>
                          <div className="val">
                            {merchantInfo.settlement_period_type === "WEEKLY"
                              ? "Weekly"
                              : merchantInfo.settlement_period_type === "DAILY"
                                ? "Daily"
                                : merchantInfo.settlement_period_type === "CUSTOM"
                                  ? "Custom"
                                  : merchantInfo.settlement_period_type === "MONTHLY"
                                    ? "Monthly"
                                    : "Custom"}
                          </div>
                        </div>
                      ) : null}
                      {/* {merchantInfo?.settlement_schedules?.frequency ? (
                      <div className="infoItem">
                        <div className="label">Settlement Frequency</div>
                        <div className="val">{merchantInfo.settlement_schedules.frequency}</div>
                      </div>
                    ) : null} */}
                      {merchantInfo?.settlement_schedules?.next_date_settlement ? (
                        <div className="infoItem">
                          <div className="label">Next Settlement Date</div>
                          <div className="val">
                            {moment(merchantInfo.settlement_schedules.next_date_settlement).format("MMM DD, YYYY [at] h:mm a")}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </>
                )}

              {/* {(merchantInfo?.created_at || merchantInfo?.updated_at) && (
              <>
                <div className="sectionSubtitle">Account Information</div>
                <div className="infoGrid">
                  {merchantInfo?.created_at ? (
                    <div className="infoItem">
                      <div className="label">Created At</div>
                      <div className="val">
                        {moment(merchantInfo.created_at).format("MMM DD, YYYY HH:mm")}
                      </div>
                    </div>
                  ) : null}
                  {merchantInfo?.updated_at ? (
                    <div className="infoItem">
                      <div className="label">Last Updated</div>
                      <div className="val">
                        {moment(merchantInfo.updated_at).format("MMM DD, YYYY HH:mm")}
                      </div>
                    </div>
                  ) : null}
                </div>
              </>
            )} */}

              {merchantInfo?.qr_code_image && (
                <div className="qrCodeSection">
                  <div className="qrCodeTitle">QR Code</div>
                  <img
                    src={merchantInfo.qr_code_image}
                    alt="QR Code"
                    className="qrCodeImage"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="right">
          <div className="infoContainer">
            <div className="label">Status</div>
            <div className="val">{merchantInfo?.status || "N/A"}</div>
          </div>
          <div className="borderContainer"></div>
          <div className="infoContainer">
            <div className="label">Active</div>
            <div className="val">{merchantInfo?.is_active ? "Yes" : "No"}</div>
          </div>
          <div className="borderContainer"></div>
          <div className="infoContainer">
            <div className="label">Total Payments</div>
            <div className="val">
              {dashboardReducer?.vendorPayments?.count || 0}
            </div>
          </div>
          {merchantInfo?.settlement_charge_percentage !== undefined ? (
            <>
              <div className="borderContainer"></div>
              <div className="infoContainer">
                <div className="label">Settlement Charge</div>
                <div className="val">{merchantInfo.settlement_charge_percentage}%</div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div className={classes.bankSection}>
        <div className="bankHeader">
          <div className="title">Merchant Bank Accounts</div>
          {/* Bank list is readable with vendors.view; Add/Edit/Delete need vendors.edit. */}
          {canEditVendors ? (
          <CommonButton
            type="button"
            text="Add Bank"
            stylevariant="primary"
            disabled={
              dashboardReducer?.listVendorBankDetailsLoading
              ||
              (bankList.length >= 1)
            }
            onClick={openAddBankDialog}
            style={{ padding: "5px 30px" }}
            fontSize="14px"
          />
          ) : null}
        </div>
        {bankList.length ? (
          <div className="bankList">
            {bankList.map((bank) => (
              <div key={getBankId(bank) || bank?.account_number || Math.random()} className="bankCard">
                <div className="bankRow">
                  <div className="label">Bank Name</div>
                  <div className="val">{bank?.bank_name || "N/A"}</div>
                </div>
                <div className="bankRow">
                  <div className="label">Account Number</div>
                  <div className="val">{bank?.account_number || "N/A"}</div>
                </div>
                <div className="bankRow">
                  <div className="label">Account Name</div>
                  <div className="val">{bank?.account_name || "N/A"}</div>
                </div>
                {bank?.code ? (
                  <div className="bankRow">
                    <div className="label">Code</div>
                    <div className="val">{bank.code}</div>
                  </div>
                ) : null}
                {canEditVendors ? (
                <div className="cardActions">
                  <CommonButton
                    text="Edit"
                    stylevariant="secondary1"
                    onClick={() => openEditBankDialog(bank)}
                    style={{ padding: "5px 20px" }}
                    fontSize="12px"
                  />
                  <CommonButton
                    text="Delete"
                    stylevariant="secondasry2"
                    onClick={() => handleDeleteBank(bank)}
                    style={{ padding: "5px 20px" }}
                    fontSize="12px"
                    loading={dashboardReducer?.deleteVendorBankDetailsLoading}
                  />
                </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="noBankMessage">No bank account details found for this merchant.</div>
        )}
      </div>

      <Dialog open={bankDialogOpen} onClose={closeBankDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{bankAction === "edit" ? "Edit Bank Details" : "Add Merchant Bank"}</DialogTitle>
        <DialogContent>
          {dashboardReducer?.addVendorBankDetailsError?.length ? (
            <div className={"error-container"}>
              {Array.isArray(dashboardReducer.addVendorBankDetailsError)
                ? dashboardReducer.addVendorBankDetailsError.map((each, index) => (
                  <div key={index}>{each}</div>
                ))
                : <div>{dashboardReducer.addVendorBankDetailsError}</div>}
            </div>
          ) : null}
          {dashboardReducer?.updateVendorBankDetailsError?.length ? (
            <div className={"error-container"}>
              {Array.isArray(dashboardReducer.updateVendorBankDetailsError)
                ? dashboardReducer.updateVendorBankDetailsError.map((each, index) => (
                  <div key={index}>{each}</div>
                ))
                : <div>{dashboardReducer.updateVendorBankDetailsError}</div>}
            </div>
          ) : null}
          <div className={classes.bankDialogForm}>
            <CommonInput
              label="Bank Name"
              placeholder="Enter bank name"
              value={bankForm.bank_name}
              onChange={(e) => handleBankFieldChange("bank_name", e.target.value)}
            />
            <CommonInput
              label="Account Number"
              placeholder="Enter account number"
              value={bankForm.account_number}
              onChange={
                (e) => {
                  if (/\d*$/.test(e.target.value))
                    handleBankFieldChange("account_number", e.target.value);
                }
              }
              helperText={function () {
                if (bankForm.account_number && bankForm.account_number?.length !== 10) {
                  return "Bank Account Number Should Be 10."
                }

                return "Must be 10 digits"
              }()}
              error={bankForm.account_number && bankForm.account_number?.length !== 10}
            />
            <CommonInput
              label="Account Name"
              placeholder="Enter account name"
              value={bankForm.account_name}
              onChange={(e) => {
                if (/\d*$/)
                  handleBankFieldChange("account_name", e.target.value)
              }}
            />
            <CommonInput
              label="Code"
              placeholder="Enter code"
              value={bankForm.code}
              onChange={(e) => handleBankFieldChange("code", e.target.value)}
              helperText="Enter Bank Code"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <CommonButton text="Cancel" stylevariant="secondary2" onClick={closeBankDialog} />
          <CommonButton
            text={bankAction === "edit" ? "Update Bank" : "Create Bank"}
            stylevariant="primary"
            onClick={handleSaveBank}
            loading={dashboardReducer?.addVendorBankDetailsLoading || dashboardReducer?.updateVendorBankDetailsLoading}
            disabled={
              dashboardReducer?.addVendorBankDetailsLoading ||
              dashboardReducer?.updateVendorBankDetailsLoading ||
              !bankForm.bank_name ||
              !bankForm.account_number ||
              !bankForm.account_name ||
              !bankForm.code ||
              (bankAction === "edit" && !editingBankId) ||
              (bankForm.account_number?.length != 10)}
          />
        </DialogActions>
      </Dialog>

      <div className={classes.tabContainer}>
        <CommonTab
          choices={[
            { value: "Sales", label: "Sales" },
            { value: "Reports", label: "Reports" },
            { value: "Settlements", label: "Settlements" },
            // Vendor fee ranges are super-only.
            ...(superUser ? [{ value: "Vendor Fees", label: "Vendor Fees" }] : []),
          ]}
          onColClick={(data) => {
            setActiveTab(data.value);
          }}
        />
      </div>

      {activeTab === "Sales" && (
        <>
          {dashboardReducer?.vendorPayments?.data?.length ? (
            <div className={classes.tableContainer}>
              <CommonTable
                columns={[
                  {
                    Header: "Employee Name",
                    accessor: "id",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data, record) => {
                      return (
                        <div className={classes.employeeName}>
                          {record?.employee_name || "N/A"}
                        </div>
                      );
                    },
                  },
                  {
                    Header: "Amount",
                    accessor: "id",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data, record) => {
                      return (
                        <div className={classes.amount}>
                          <span className={classes.currency}>{record?.company?.currency || "NGN"}</span> {moneyValue(record?.amount || 0)}
                        </div>
                      );
                    },
                  },
                  {
                    Header: "Net Amount",
                    accessor: "net",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data, record) => {
                      return (
                        <div className={classes.amount}>
                          <span className={classes.currency}>{record?.currency || "NGN"}</span> {moneyValue(record?.net || 0)}
                        </div>
                      );
                    },
                  },
                  {
                    Header: "Charge Amount",
                    accessor: "charge_amount",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data, record) => {
                      return (
                        <div className={classes.amount}>
                          <span className={classes.currency}>{record?.currency || "NGN"}</span> {moneyValue(record?.charge_amount || 0)}
                        </div>
                      );
                    },
                  },
                  {
                    Header: "Total Amount",
                    accessor: "total",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data, record) => {
                      return (
                        <div className={classes.amount}>
                          <span className={classes.currency}>{record?.currency || "NGN"}</span> {moneyValue(record?.total || 0)}
                        </div>
                      );
                    },
                  },
                  {
                    Header: "Reference",
                    accessor: "id",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data, record) => {
                      return (
                        <div className={classes.reference}>
                          {record?.reference || "N/A"}
                        </div>
                      );
                    },
                  },
                  {
                    Header: "Intent",
                    accessor: "id",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data, record) => {
                      const intent = getIntentDisplay(record?.intent || "N/A");
                      return (
                        <div>
                          <span className={classes.intent}>
                            {intent || "N/A"}
                          </span>
                        </div>
                      );
                    },
                  },
                  {
                    Header: "Date",
                    accessor: "id",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data, record) => {
                      return (
                        <div className={classes.date}>
                          {record?.created_at
                            ? moment(record.created_at).format("MMM DD, YYYY")
                            : "N/A"}
                        </div>
                      );
                    },
                  },
                ]}
                data={dashboardReducer?.vendorPayments?.data}
              />
              {dashboardReducer?.vendorPayments?.data?.length ? (
                <div className={"pagination"}>
                  <Pagination
                    page={page}
                    count={totalPage}
                    showFirstButton
                    showLastButton
                    onChange={(event, value) => {
                      setPage(value);
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}

      {activeTab === "Reports" && (
        <div className={classes.reportsContainer}>
          <Loading showLoader={vendorDashboardReducer?.loading} />
          {vendorDashboardReducer?.error?.length ? (
            <div className={"error-container"}>
              {Array.isArray(vendorDashboardReducer.error)
                ? vendorDashboardReducer.error.map((each, index) => (
                  <div key={index}>{each}</div>
                ))
                : <div>{vendorDashboardReducer.error}</div>}
            </div>
          ) : null}

          <div className={classes.periodSelector}>
            <div
              className={`periodButton ${selectedPeriod === "today" ? "active" : ""}`}
              onClick={() => setSelectedPeriod("today")}
            >
              Today
            </div>
            <div
              className={`periodButton ${selectedPeriod === "week" ? "active" : ""}`}
              onClick={() => setSelectedPeriod("week")}
            >
              This Week
            </div>
            <div
              className={`periodButton ${selectedPeriod === "month" ? "active" : ""}`}
              onClick={() => setSelectedPeriod("month")}
            >
              This Month
            </div>
            <div
              className={`periodButton ${selectedPeriod === "custom" ? "active" : ""}`}
              onClick={() => setSelectedPeriod("custom")}
            >
              Custom Range
            </div>
          </div>

          {selectedPeriod === "custom" && (
            <div style={{ marginBottom: "30px" }}>
              <DragDatePreviewer
                startDateValue={startDate || defaultStartDate}
                setStartDateValue={(date) => {
                  setStartDate(new Date(date).toISOString().split("T")[0]);
                }}
                endDateValue={endDate || defaultEndDate}
                setEndDateValue={(date) => {
                  setEndDate(new Date(date).toISOString().split("T")[0]);
                }}
                absoluteCalendar
              />
            </div>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <div className={classes.summaryCard}>
                <div className="cardTitle">Transaction Summary</div>
                <div className="summaryRow">
                  <div className="label">Total Transactions</div>
                  <div className="value">
                    {vendorDashboardReducer?.vendorOverview?.payment_count || 0}
                  </div>
                </div>
                <div className="summaryRow">
                  <div className="label">Total Sales Volume</div>
                  <div className="value">
                    <span className="currency">
                      {vendorDashboardReducer?.vendorOverview?.currency || "NGN"}
                    </span>
                    {moneyValue(vendorDashboardReducer?.vendorOverview?.total_amount_received || 0)}
                  </div>
                </div>
                <div className="summaryRow">
                  <div className="label">Total Discounts Applied</div>
                  <div className="value">
                    <span className="currency">
                      {vendorDashboardReducer?.vendorOverview?.currency || "NGN"}
                    </span>
                    {moneyValue(vendorDashboardReducer?.vendorOverview?.settlement_charge || 0)}
                  </div>
                </div>
                <div className="summaryRow">
                  <div className="label">Net Earnings</div>
                  <div className="value">
                    <span className="currency">
                      {vendorDashboardReducer?.vendorOverview?.currency || "NGN"}
                    </span>
                    {moneyValue(vendorDashboardReducer?.vendorOverview?.settlement_net || 0)}
                  </div>
                </div>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={classes.summaryCard}>
                <div className="cardTitle">Performance Indicators</div>
                <div className="summaryRow">
                  <div className="label">Average Transaction Value</div>
                  <div className="value">
                    <span className="currency">
                      {vendorDashboardReducer?.vendorOverview?.currency || "NGN"}
                    </span>
                    {vendorDashboardReducer?.vendorOverview?.payment_count > 0
                      ? moneyValue(
                        (vendorDashboardReducer?.vendorOverview?.total_amount_received || 0) /
                        vendorDashboardReducer.vendorOverview.payment_count
                      )
                      : moneyValue(0)}
                  </div>
                </div>
                <div className="summaryRow">
                  <div className="label">Discount Rate</div>
                  <div className="value">
                    {vendorDashboardReducer?.vendorOverview?.total_amount_received > 0
                      ? (
                        ((vendorDashboardReducer?.vendorOverview?.settlement_charge || 0) /
                          vendorDashboardReducer.vendorOverview.total_amount_received) *
                        100
                      ).toFixed(2) + "%"
                      : "0%"}
                  </div>
                </div>
                <div className="summaryRow">
                  <div className="label">Active Customers</div>
                  <div className="value">
                    {vendorDashboardReducer?.vendorOverview?.active_customers_count || 0}
                  </div>
                </div>
                <div className="summaryRow">
                  <div className="label">New vs Returning</div>
                  <div className="value">
                    {vendorDashboardReducer?.vendorOverview?.new_employees_count || 0} /{" "}
                    {vendorDashboardReducer?.vendorOverview?.returning_employees_count || 0}
                  </div>
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      )}



      {activeTab === "Settlements" && (
        <>
          <div className={classes.aggregateSettlementSection}>
            <div className="aggregateSettlementHeader">
              <div className="title">Merchant Settlement Aggregate</div>
              {canEditVendors ? (
              <CommonButton
                type="button"
                text="Settlement Aggregate"
                stylevariant="primary"
                disabled={
                  dashboardReducer?.vendorSettlementTriggerAggregateLoading
                }
                onClick={handleOpenSettlementDialogPreview}
                loading={
                  dashboardReducer?.vendorSettlementTriggerAggregateLoading
                }
                style={{ padding: "5px 30px" }}
                fontSize="14px"
              />
              ) : null}
            </div>

            <Dialog open={settlementAggregate.open} onClose={handleCloseSettlementDialogPreview} maxWidth="sm" fullWidth>
              <DialogTitle>Preview Settlement Aggregate</DialogTitle>
              <DialogContent>
                {dashboardReducer?.vendorSettlementTriggerAggregateError?.length ? (
                  <div className={"error-container"}>
                    {Array.isArray(dashboardReducer.vendorSettlementTriggerAggregateError)
                      ? dashboardReducer.vendorSettlementTriggerAggregateError.map((each, index) => (
                        <div key={index}>{each}</div>
                      ))
                      : <div>{dashboardReducer.vendorSettlementTriggerAggregateError}</div>}
                  </div>
                ) : null}

                <div>
                  <p><strong>Gross Amount: </strong> {formatCurrency(dashboardReducer?.vendorSettlementTriggerAggregateSuccess?.data?.gross_amount || 0)}</p>
                  <p><strong>Net Amount: </strong> {formatCurrency(dashboardReducer?.vendorSettlementTriggerAggregateSuccess?.data?.net_amount || 0)}</p>
                  <p><strong>Charge Amount: </strong> {formatCurrency(dashboardReducer?.vendorSettlementTriggerAggregateSuccess?.data?.total_discount || 0)}</p>
                  <p><strong>Start Date: </strong> {dashboardReducer?.vendorSettlementTriggerAggregateSuccess?.data?.start_date || "N/A"}</p>
                  <p><strong>End Date: </strong> {dashboardReducer?.vendorSettlementTriggerAggregateSuccess?.data?.end_date || "N/A"}</p>
                  <p><strong> Total No. of Transactions: </strong> {dashboardReducer?.vendorSettlementTriggerAggregateSuccess?.data?.total_payments || 0}</p>
                </div>

              </DialogContent>
              <DialogActions>
                <CommonButton text="Cancel" stylevariant="secondary2" onClick={handleCloseSettlementDialogPreview} />
                <CommonButton
                  text={"Settle Outstanding"}
                  stylevariant="primary"
                  onClick={() => {
                    vendorSettlementTriggerMethod({ vendor_id: merchantInfo?.id });

                  }}
                  loading={
                    dashboardReducer?.vendorSettlementTriggerAggregateLoading || dashboardReducer?.vendorSettlementTriggerLoading
                  }
                  disabled={
                    dashboardReducer?.vendorSettlementTriggerAggregateLoading ||
                    dashboardReducer?.vendorSettlementTriggerLoading ||
                    bankList.length <= 0 ||
                    (dashboardReducer?.vendorSettlementTriggerAggregateSuccess?.data?.gross_amount || 0) < 1 ||
                    dashboardReducer.vendorSettlementTriggerAggregateError

                  }
                />
              </DialogActions>
            </Dialog>
          </div>
          {dashboardReducer?.vendorSettlements?.data?.length ? (
            <div className={classes.tableContainer}>
              <CommonTable
                columns={[
                  {
                    Header: "Payment Reference",
                    accessor: "payment_reference",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data) => {
                      return (
                        <div className={classes.employeeName}>
                          {data || "N/A"}
                        </div>
                      );
                    },
                  },
                  {
                    Header: "Charge Amount",
                    accessor: "total_discount",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data, record) => {
                      const chargeAmount = record?.total_discount || 0;
                      return (
                        <div className={classes.amount}>
                          <span className={classes.currency}>NGN</span>
                          {moneyValue(chargeAmount)}
                        </div>
                      );
                    },
                  },
                  {
                    Header: "Amount",
                    accessor: "net_amount",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data, record) => {
                      return (
                        <div className={classes.amount}>
                          <span className={classes.currency}>NGN</span>
                          {moneyValue(record?.net_amount || 0)}
                        </div>
                      );
                    },
                  },
                  {
                    Header: "Status",
                    accessor: "status",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data) => {
                      const status = data?.toLowerCase() || "";
                      let IconComponent = null;
                      let iconColor = "";
                      let tooltipTitle = data || "N/A";

                      if (status === "pending") {
                        IconComponent = HourglassEmpty;
                        iconColor = theme.colors.DodgerBlue;
                      } else if (status === "processing") {
                        IconComponent = Autorenew;
                        iconColor = theme.colors.DodgerBlue;
                      } else if (status === "completed") {
                        IconComponent = CheckCircle;
                        iconColor = theme.colors.MintGreen;
                      }

                      if (!IconComponent) {
                        return <div className={classes.employeeName}>N/A</div>;
                      }

                      return (
                        <Tooltip title={tooltipTitle} arrow>
                          <IconComponent
                            style={{
                              color: iconColor,
                              fontSize: "20px",
                              cursor: "pointer"
                            }}
                          />
                        </Tooltip>
                      );
                    },
                  },
                  {
                    Header: "Period Type",
                    accessor: "period_type",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data) => {
                      return (
                        <div className={classes.employeeName}>
                          {data || "N/A"}
                        </div>
                      );
                    },
                  },
                  {
                    Header: "Start Date",
                    accessor: "start_date",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data) => {
                      return (
                        <div className={classes.date}>
                          {data ? moment(data).format("DD/MM/YYYY") : "N/A"}
                        </div>
                      );
                    },
                  },
                  {
                    Header: "End Date",
                    accessor: "end_date",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data) => {
                      return (
                        <div className={classes.date}>
                          {data ? moment(data).format("DD/MM/YYYY") : "N/A"}
                        </div>
                      );
                    },
                  },
                  {
                    Header: "Completed At",
                    accessor: "completed_at",
                    HeaderCell: (data) => {
                      return <div className={classes.headerName}>{data}</div>;
                    },
                    Cell: (data) => {
                      return (
                        <div className={classes.date}>
                          {data ? moment(data).format("DD/MM/YYYY HH:mm") : "N/A"}
                        </div>
                      );
                    },
                  },
                ]}
                data={dashboardReducer?.vendorSettlements?.data || []}
                loading={dashboardReducer?.loading}
              />
              {settlementsTotalPage > 1 && (
                <div className={classes.pagination}>
                  <Pagination
                    count={settlementsTotalPage}
                    page={settlementsPage}
                    showFirstButton
                    showLastButton
                    onChange={(event, value) => setSettlementsPage(value)}
                  />
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "60px 20px",
                color: theme.colors.Violet,
                fontFamily: theme.fontFamily.MARKPRO,
              }}
            >
              No settlements found
            </div>
          )}
        </>
      )}

      {
        superUser && activeTab === "Vendor Fees" && (
          <>
            <VendorFeesTab vendor_id={merchantInfo?.id} />
          </>
        )
      }
    </div>
  );
}

export default MerchantDetail;
